import * as FC from "@fluentui/react-components";
import * as FDP from "@fluentui/react-datepicker-compat";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { toDateString } from "../../date_utils";
import { useToast } from "../../hooks/useToast";
import { apiClient } from "../../services/api";
import {
    createEquipment,
    equipmentErrorCodes,
    equipmentErrorTrace,
    equipmentOperationalStatusLabel,
    equipmentTypeLabel,
    equipmentTypeOptions,
    formatDateOnly,
    getEquipment,
    isEquipmentErrorCode,
    parseDateOnly,
    toDateOnly,
    updateEquipment,
} from "../../services/equipment_service";

const useStyles = FC.makeStyles({
    form: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("12px"),
    },
    row: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        ...FC.shorthands.gap("10px"),
    },
    actions: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...FC.shorthands.gap("8px"),
    },
    mono: {
        fontFamily: "Consolas, monospace",
    },
    hint: {
        color: FC.tokens.colorNeutralForeground3,
    },
});

const emptyForm = {
    tenantId: "",
    assetCode: "",
    name: "",
    type: "",
    serialNumber: "",
    manufacturer: "",
    model: "",
    purchaseDate: null,
    commissionedDate: null,
    warrantyStartDate: null,
    warrantyEndDate: null,
    warrantyProvider: "",
    maintenanceIntervalDays: "",
    nextMaintenanceDate: null,
    location: "",
    custodianUserId: "",
    notes: "",
};

const toFormValues = (detail) => {
    if (!detail) {
        return { ...emptyForm };
    }
    return {
        tenantId: detail.tenantId ? String(detail.tenantId) : "",
        assetCode: detail.assetCode || "",
        name: detail.name || "",
        type: detail.type || "",
        serialNumber: detail.serialNumber || "",
        manufacturer: detail.manufacturer || "",
        model: detail.model || "",
        purchaseDate: parseDateOnly(detail.purchaseDate),
        commissionedDate: parseDateOnly(detail.commissionedDate),
        warrantyStartDate: parseDateOnly(detail.warrantyStartDate),
        warrantyEndDate: parseDateOnly(detail.warrantyEndDate),
        warrantyProvider: detail.warrantyProvider || "",
        maintenanceIntervalDays:
            detail.maintenanceIntervalDays === null || detail.maintenanceIntervalDays === undefined
                ? ""
                : String(detail.maintenanceIntervalDays),
        nextMaintenanceDate: parseDateOnly(detail.nextMaintenanceDate),
        location: detail.location || "",
        custodianUserId: detail.custodianUserId ? String(detail.custodianUserId) : "",
        notes: detail.notes || "",
    };
};

const specificationsToText = (specifications) => {
    if (!specifications) {
        return "";
    }
    try {
        return JSON.stringify(specifications, null, 2);
    } catch (error) {
        console.error("Failed to render specifications:", error);
        return "";
    }
};

const trimmedOrNull = (value) => {
    const text = (value ?? "").toString().trim();
    return text === "" ? null : text;
};

const DateField = ({ label, value, onChange, hint, error, disabled }) => (
    <FC.Field
        label={label}
        hint={hint}
        validationState={error ? "error" : "none"}
        validationMessage={error || undefined}
    >
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <FDP.DatePicker
                style={{ flex: 1 }}
                disabled={disabled}
                value={value || null}
                placeholder="选择日期"
                formatDate={toDateString}
                onSelectDate={(date) => onChange(date || null)}
            />
            <FC.Button
                appearance="subtle"
                aria-label={`清除${label}`}
                disabled={disabled || !value}
                icon={<IC.DismissRegular />}
                onClick={() => onChange(null)}
            />
        </div>
    </FC.Field>
);

/**
 * Create / edit form for the equipment master record.
 *
 * `operationalStatus`, `lastMaintenanceDate` and `nextMaintenanceDate` are server-owned:
 * status moves through the dedicated endpoint, the maintenance cache is recalculated from
 * service records and schedule adjustments. Only the initial plan on create is accepted here.
 */
export const EquipmentForm = ({ item, isAdmin = false, onSaved, onCancel }) => {
    const styles = useStyles();
    const showToast = useToast();
    const submittingRef = useRef(false);

    const [detail, setDetail] = useState(null);
    const [form, setForm] = useState(toFormValues(null));
    const [specificationsText, setSpecificationsText] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [formError, setFormError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pendingDateAnomaly, setPendingDateAnomaly] = useState(null);
    const [versionConflict, setVersionConflict] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [users, setUsers] = useState([]);

    const applyDetail = useCallback((loaded) => {
        setDetail(loaded);
        setForm(toFormValues(loaded));
        setSpecificationsText(specificationsToText(loaded?.specifications));
        setFieldErrors({});
        setFormError(null);
    }, []);

    const loadDetail = useCallback(
        async (id) => {
            setLoading(true);
            try {
                const loaded = await getEquipment(id);
                applyDetail(loaded);
            } catch (error) {
                console.error("Failed to load equipment detail:", error);
                setFormError({ message: error?.message || "加载器材详情失败", traceId: error?.traceId });
            } finally {
                setLoading(false);
            }
        },
        [applyDetail]
    );

    useEffect(() => {
        if (item?.id) {
            loadDetail(item.id);
        } else {
            applyDetail(null);
        }
    }, [applyDetail, item, loadDetail]);

    useEffect(() => {
        if (!isAdmin) {
            return;
        }
        apiClient
            .getTenants({ status: "active" })
            .then((items) => setTenants(items || []))
            .catch((error) => console.error("Failed to load tenants:", error));
    }, [isAdmin]);

    useEffect(() => {
        apiClient
            .getUsers()
            .then((items) => setUsers(items || []))
            .catch((error) => console.error("Failed to load users:", error));
    }, []);

    const isEdit = Boolean(detail?.id);
    const selectedTenantId = isEdit ? detail?.tenantId : Number(form.tenantId) || null;
    // The custodian must belong to the same tenant as the equipment (backend rule 7.2).
    const custodianCandidates = selectedTenantId
        ? users.filter((user) => user.tenantId === selectedTenantId)
        : users;

    const setField = (key, value) => {
        setForm((previous) => ({ ...previous, [key]: value }));
        setFieldErrors((previous) => {
            if (!previous[key]) {
                return previous;
            }
            const next = { ...previous };
            delete next[key];
            return next;
        });
    };

    const text = (key) => ({
        value: form[key] ?? "",
        onChange: (_evt, data) => setField(key, data.value ?? ""),
    });

    const parseSpecifications = () => {
        const raw = specificationsText.trim();
        if (!raw) {
            return { value: null };
        }
        try {
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
                return { error: "扩展规格必须是 JSON 对象，例如 {\"firmwareVersion\": \"10.01\"}" };
            }
            return { value: parsed };
        } catch (error) {
            return { error: "扩展规格不是合法的 JSON" };
        }
    };

    const validate = () => {
        const errors = {};
        if (!trimmedOrNull(form.assetCode)) {
            errors.assetCode = "资产编号必填";
        }
        if (!trimmedOrNull(form.name)) {
            errors.name = "名称必填";
        }
        if (!form.type) {
            errors.type = "器材类型必填";
        }
        if (!isEdit && isAdmin && !form.tenantId) {
            errors.tenantId = "管理员新增器材必须指定租户";
        }
        if (form.maintenanceIntervalDays !== "") {
            const interval = Number(form.maintenanceIntervalDays);
            if (!Number.isInteger(interval) || interval <= 0) {
                errors.maintenanceIntervalDays = "保养周期必须是大于 0 的整数天数";
            }
        }
        if (form.warrantyStartDate && form.warrantyEndDate && form.warrantyEndDate < form.warrantyStartDate) {
            errors.warrantyEndDate = "保修截止日期不能早于保修开始日期";
        }
        const specifications = parseSpecifications();
        if (specifications.error) {
            errors.specifications = specifications.error;
        }
        return { errors, specifications: specifications.value };
    };

    const buildPayload = (specifications, confirmDateAnomaly) => {
        const shared = {
            assetCode: trimmedOrNull(form.assetCode),
            name: trimmedOrNull(form.name),
            type: form.type,
            serialNumber: trimmedOrNull(form.serialNumber),
            manufacturer: trimmedOrNull(form.manufacturer),
            model: trimmedOrNull(form.model),
            purchaseDate: toDateOnly(form.purchaseDate),
            commissionedDate: toDateOnly(form.commissionedDate),
            warrantyStartDate: toDateOnly(form.warrantyStartDate),
            warrantyEndDate: toDateOnly(form.warrantyEndDate),
            warrantyProvider: trimmedOrNull(form.warrantyProvider),
            maintenanceIntervalDays:
                form.maintenanceIntervalDays === "" ? null : Number(form.maintenanceIntervalDays),
            location: trimmedOrNull(form.location),
            custodianUserId: form.custodianUserId ? Number(form.custodianUserId) : null,
            specifications,
            notes: trimmedOrNull(form.notes),
            confirmDateAnomaly: Boolean(confirmDateAnomaly),
        };

        if (isEdit) {
            // No operationalStatus / lastMaintenanceDate / nextMaintenanceDate: server-owned fields.
            return { ...shared, version: detail.version };
        }

        return {
            ...shared,
            tenantId: isAdmin && form.tenantId ? Number(form.tenantId) : undefined,
            nextMaintenanceDate: toDateOnly(form.nextMaintenanceDate),
        };
    };

    /**
     * 后端字段错误的键是 DTO 属性名（PascalCase），表单状态用 camelCase；
     * 同一字段可能有多条消息，合并成一行展示。
     */
    const mapFieldErrorDetails = (details) => {
        const mapped = {};
        Object.entries(details || {}).forEach(([key, messages]) => {
            if (!key) {
                return;
            }
            const field = key.charAt(0).toLowerCase() + key.slice(1);
            const text = Array.isArray(messages) ? messages.filter(Boolean).join("；") : String(messages || "");
            if (text) {
                mapped[field] = text;
            }
        });
        return mapped;
    };

    const handleError = (error) => {
        const traceId = error?.traceId;
        const message = error?.message || "保存器材失败";

        // 服务端校验：把每条消息放到对应字段旁，而不是只弹一条笼统横幅。
        if (isEquipmentErrorCode(error, equipmentErrorCodes.RequestValidationFailed)) {
            const mapped = mapFieldErrorDetails(error?.details);
            if (Object.keys(mapped).length > 0) {
                setFieldErrors((previous) => ({ ...previous, ...mapped }));
            }
            setFormError({ message, traceId });
            return;
        }

        if (isEquipmentErrorCode(error, equipmentErrorCodes.VersionConflict)) {
            setVersionConflict({ message, traceId });
            return;
        }
        if (isEquipmentErrorCode(error, equipmentErrorCodes.AssetCodeDuplicate)) {
            setFieldErrors((previous) => ({ ...previous, assetCode: message }));
            setFormError({ message, traceId });
            return;
        }
        if (isEquipmentErrorCode(error, equipmentErrorCodes.SerialNumberDuplicate)) {
            setFieldErrors((previous) => ({ ...previous, serialNumber: message }));
            setFormError({ message, traceId });
            return;
        }
        if (isEquipmentErrorCode(error, equipmentErrorCodes.CustodianInvalid)) {
            setFieldErrors((previous) => ({ ...previous, custodianUserId: message }));
            setFormError({ message, traceId });
            return;
        }
        if (isEquipmentErrorCode(error, equipmentErrorCodes.InvalidSpecifications)) {
            setFieldErrors((previous) => ({ ...previous, specifications: message }));
            setFormError({ message, traceId });
            return;
        }
        if (isEquipmentErrorCode(error, equipmentErrorCodes.InvalidMaintenanceInterval)) {
            setFieldErrors((previous) => ({ ...previous, maintenanceIntervalDays: message }));
            setFormError({ message, traceId });
            return;
        }
        if (isEquipmentErrorCode(error, equipmentErrorCodes.InvalidDateRange)) {
            setFieldErrors((previous) => ({ ...previous, warrantyEndDate: message }));
            setFormError({ message, traceId });
            return;
        }

        setFormError({ message, traceId });
    };

    const submit = async (confirmDateAnomaly = false) => {
        // Double-submit guard: the ref blocks the second call before React re-renders.
        if (submittingRef.current) {
            return;
        }

        const { errors, specifications } = validate();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        submittingRef.current = true;
        setSubmitting(true);
        setFormError(null);
        try {
            const payload = buildPayload(specifications, confirmDateAnomaly);
            const saved = isEdit
                ? await updateEquipment(detail.id, payload)
                : await createEquipment(payload);
            applyDetail(saved);
            showToast(isEdit ? "器材已保存" : "器材已创建", "success");
            onSaved?.({ close: true, item: saved });
        } catch (error) {
            console.error("Failed to save equipment:", error);
            if (isEquipmentErrorCode(error, equipmentErrorCodes.DateAnomalyRequiresConfirmation)) {
                setPendingDateAnomaly({
                    message: error?.message || "启用日期早于购买日期，请确认。",
                    traceId: error?.traceId,
                });
            } else {
                handleError(error);
            }
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    const confirmDateAnomaly = async () => {
        setPendingDateAnomaly(null);
        await submit(true);
    };

    return (
        <div className={styles.form}>
            {formError && (
                <FC.MessageBar intent="error">
                    <FC.MessageBarBody>
                        {formError.message}
                        {formError.traceId ? `（${equipmentErrorTrace(formError)}）` : ""}
                    </FC.MessageBarBody>
                </FC.MessageBar>
            )}

            <FC.Divider>基本信息</FC.Divider>
            {isAdmin && !isEdit && (
                <FC.Field
                    label="租户"
                    required
                    validationState={fieldErrors.tenantId ? "error" : "none"}
                    validationMessage={fieldErrors.tenantId}
                >
                    <FC.Dropdown
                        selectedOptions={form.tenantId ? [form.tenantId] : []}
                        value={
                            tenants.find((tenant) => String(tenant.id) === form.tenantId)?.name || ""
                        }
                        placeholder="选择租户"
                        onOptionSelect={(_, data) => {
                            // 负责人必须属于所选租户；换租户后下拉会清空显示，
                            // 但若不同时清掉已选的 id，保存时会带着上一个租户的用户提交并被后端拒绝。
                            setField("tenantId", data.optionValue);
                            setField("custodianUserId", "");
                        }}
                    >
                        {tenants.map((tenant) => (
                            <FC.Option key={tenant.id} value={String(tenant.id)} text={tenant.name}>
                                {`${tenant.seq} - ${tenant.name}`}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </FC.Field>
            )}
            {isEdit && (
                <FC.Field label="租户">
                    <FC.Input readOnly value={detail?.tenantName || `Tenant ${detail?.tenantId}`} />
                </FC.Field>
            )}
            <div className={styles.row}>
                <FC.Field
                    label="资产编号"
                    required
                    hint="同租户内唯一，保存时统一转为大写"
                    validationState={fieldErrors.assetCode ? "error" : "none"}
                    validationMessage={fieldErrors.assetCode}
                >
                    <FC.Input className={styles.mono} {...text("assetCode")} />
                </FC.Field>
                <FC.Field
                    label="名称"
                    required
                    validationState={fieldErrors.name ? "error" : "none"}
                    validationMessage={fieldErrors.name}
                >
                    <FC.Input {...text("name")} />
                </FC.Field>
            </div>
            <div className={styles.row}>
                <FC.Field
                    label="器材类型"
                    required
                    validationState={fieldErrors.type ? "error" : "none"}
                    validationMessage={fieldErrors.type}
                >
                    <FC.Dropdown
                        selectedOptions={form.type ? [form.type] : []}
                        value={equipmentTypeLabel(form.type)}
                        placeholder="选择类型"
                        onOptionSelect={(_, data) => setField("type", data.optionValue)}
                    >
                        {equipmentTypeOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </FC.Field>
                <FC.Field
                    label="序列号"
                    hint="厂家序列号，同租户同类型下唯一"
                    validationState={fieldErrors.serialNumber ? "error" : "none"}
                    validationMessage={fieldErrors.serialNumber}
                >
                    <FC.Input className={styles.mono} {...text("serialNumber")} />
                </FC.Field>
            </div>
            <div className={styles.row}>
                <FC.Field label="品牌">
                    <FC.Input {...text("manufacturer")} />
                </FC.Field>
                <FC.Field label="型号">
                    <FC.Input {...text("model")} />
                </FC.Field>
            </div>

            <FC.Divider>使用信息</FC.Divider>
            <div className={styles.row}>
                <FC.Field label="运行状态" hint="由状态变更、维保和巡检流程维护，表单不直接修改">
                    <FC.Input
                        readOnly
                        value={
                            isEdit
                                ? equipmentOperationalStatusLabel(detail?.operationalStatus)
                                : "新增器材默认为可用"
                        }
                    />
                </FC.Field>
                <FC.Field
                    label="负责人"
                    validationState={fieldErrors.custodianUserId ? "error" : "none"}
                    validationMessage={fieldErrors.custodianUserId}
                >
                    <FC.Dropdown
                        selectedOptions={form.custodianUserId ? [form.custodianUserId] : []}
                        value={
                            custodianCandidates.find(
                                (user) => String(user.id) === form.custodianUserId
                            )?.name || ""
                        }
                        placeholder="选择负责人"
                        onOptionSelect={(_, data) => setField("custodianUserId", data.optionValue)}
                    >
                        <FC.Option value="">未指定</FC.Option>
                        {custodianCandidates.map((user) => (
                            <FC.Option key={user.id} value={String(user.id)} text={user.name}>
                                {`${user.name}${user.department ? ` · ${user.department}` : ""}`}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </FC.Field>
            </div>
            <FC.Field label="存放位置">
                <FC.Input {...text("location")} />
            </FC.Field>
            <div className={styles.row}>
                <DateField
                    label="购买日期"
                    value={form.purchaseDate}
                    onChange={(date) => setField("purchaseDate", date)}
                    error={fieldErrors.purchaseDate}
                />
                <DateField
                    label="启用日期"
                    value={form.commissionedDate}
                    onChange={(date) => setField("commissionedDate", date)}
                    error={fieldErrors.commissionedDate}
                />
            </div>

            <FC.Divider>保修与保养</FC.Divider>
            <div className={styles.row}>
                <DateField
                    label="保修开始日期"
                    value={form.warrantyStartDate}
                    onChange={(date) => setField("warrantyStartDate", date)}
                    error={fieldErrors.warrantyStartDate}
                />
                <DateField
                    label="保修截止日期"
                    value={form.warrantyEndDate}
                    onChange={(date) => setField("warrantyEndDate", date)}
                    error={fieldErrors.warrantyEndDate}
                />
            </div>
            <div className={styles.row}>
                <FC.Field label="保修服务商">
                    <FC.Input {...text("warrantyProvider")} />
                </FC.Field>
                <FC.Field
                    label="保养周期（天）"
                    validationState={fieldErrors.maintenanceIntervalDays ? "error" : "none"}
                    validationMessage={fieldErrors.maintenanceIntervalDays}
                >
                    <FC.Input type="number" min={1} {...text("maintenanceIntervalDays")} />
                </FC.Field>
            </div>
            {isEdit ? (
                <div className={styles.row}>
                    <FC.Field label="最近保养日期" hint="由已完成的保养记录维护">
                        <FC.Input readOnly value={formatDateOnly(detail?.lastMaintenanceDate) || "未记录"} />
                    </FC.Field>
                    <FC.Field label="下次保养日期" hint="由保养记录或保养计划调整维护">
                        <FC.Input readOnly value={formatDateOnly(detail?.nextMaintenanceDate) || "未设置"} />
                    </FC.Field>
                </div>
            ) : (
                <DateField
                    label="初始下次保养日期"
                    hint="仅新增时可填写，将记录为一条保养计划调整事件"
                    value={form.nextMaintenanceDate}
                    onChange={(date) => setField("nextMaintenanceDate", date)}
                    error={fieldErrors.nextMaintenanceDate}
                />
            )}

            <FC.Divider>扩展规格</FC.Divider>
            <FC.Field
                label="扩展规格 JSON"
                hint='类型特有参数，必须是 JSON 对象，例如 {"firmwareVersion": "10.01.0502"}'
                validationState={fieldErrors.specifications ? "error" : "none"}
                validationMessage={fieldErrors.specifications}
            >
                <FC.Textarea
                    className={styles.mono}
                    rows={5}
                    resize="vertical"
                    value={specificationsText}
                    onChange={(_, data) => {
                        setSpecificationsText(data.value ?? "");
                        setFieldErrors((previous) => {
                            if (!previous.specifications) {
                                return previous;
                            }
                            const next = { ...previous };
                            delete next.specifications;
                            return next;
                        });
                    }}
                />
            </FC.Field>

            <FC.Divider>备注</FC.Divider>
            <FC.Field label="备注">
                <FC.Textarea rows={3} resize="vertical" {...text("notes")} />
            </FC.Field>

            <FC.Divider />
            <div className={styles.actions}>
                {onCancel && (
                    <FC.Button disabled={submitting} onClick={onCancel}>
                        取消
                    </FC.Button>
                )}
                <FC.Button
                    icon={<IC.SaveRegular />}
                    appearance="primary"
                    disabled={submitting || loading}
                    onClick={() => submit(false)}
                >
                    {submitting ? "保存中..." : isEdit ? "保存" : "创建器材"}
                </FC.Button>
                {isEdit && (
                    <FC.Caption1 className={styles.hint}>{`当前版本 v${detail?.version}`}</FC.Caption1>
                )}
            </div>

            <FC.Dialog
                open={Boolean(pendingDateAnomaly)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setPendingDateAnomaly(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>日期需要确认</FC.DialogTitle>
                        <FC.DialogContent>
                            {pendingDateAnomaly?.message}
                            <br />
                            历史补录可能确实如此。确认后将按当前填写的日期保存。
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setPendingDateAnomaly(null)}>
                                返回修改
                            </FC.Button>
                            <FC.Button appearance="primary" disabled={submitting} onClick={confirmDateAnomaly}>
                                确认并保存
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            <FC.Dialog
                open={Boolean(versionConflict)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setVersionConflict(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>记录已被其他用户修改</FC.DialogTitle>
                        <FC.DialogContent>
                            {versionConflict?.message || "记录已被其他用户修改。"}
                            <br />
                            {versionConflict?.traceId
                                ? `${equipmentErrorTrace(versionConflict)}。`
                                : ""}
                            重新加载会丢弃当前未保存的修改。
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setVersionConflict(null)}>
                                保留我的修改
                            </FC.Button>
                            <FC.Button
                                appearance="primary"
                                onClick={() => {
                                    setVersionConflict(null);
                                    if (detail?.id) {
                                        loadDetail(detail.id);
                                    }
                                }}
                            >
                                重新加载
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>
        </div>
    );
};
